import React, { useEffect, useState } from 'react';
import { Space, Table, Button, Modal, Select, Alert } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import InputNumber from 'components/input-number';
import { formatCurrency } from 'utils/number';

const PriceEdit = ({ value = [], onChange, original_price = 0, onChangeAll, disabled = false }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pricelist, setPriceList] = useState([]);

  useEffect(() => {
    setPriceList([...(value || [])]);
  }, [value]);

  const handleOk = () => {
    setIsModalOpen(false);
    onChange(pricelist);
  };

  const onAddPrice = () => {
    if (pricelist.length > 0) {
      const last = pricelist[pricelist.length - 1];
      const newPrices = { min: last.max, max: last.max + 10, price: last.price - 10000 };
      setPriceList([...pricelist, newPrices]);
    } else {
      const newPrices = { min: 1, max: 5, price: original_price };
      setPriceList([...pricelist, newPrices]);
    }
  };

  const deletePriceAt = (index) => {
    const newPriceList = [...pricelist];
    newPriceList.splice(index, 1);
    setPriceList(newPriceList);
  };

  const updatePriceAt = (index, name, nextValue) => {
    const newPriceList = [...pricelist];
    newPriceList[index][name] = nextValue;
    setPriceList(newPriceList);
  };

  const btnAllClick = () => {
    setIsModalOpen(false);
    onChangeAll(pricelist);
  };

  return (
    <div>
      <Space>
        {!disabled && <Button type='text' icon={<EditOutlined />} onClick={() => setIsModalOpen(true)} />}
      </Space>

      <Modal
        title='Sửa giá'
        open={isModalOpen}
        onOk={handleOk}
        onCancel={() => setIsModalOpen(false)}
        footer={
          <Space>
            <Button type='primary' onClick={onAddPrice}>Thêm khoảng giá</Button>
            <Button type='primary' onClick={btnAllClick}>Áp dụng chung</Button>
            <Button type='primary' onClick={handleOk}>Áp dụng</Button>
          </Space>
        }
      >
        <Table
          pagination={false}
          dataSource={pricelist}
          rowKey={(_, i) => i}
          columns={[
            {
              title: 'Từ',
              render: (_, { min }, i) => <InputNumber value={min} onChange={(e) => updatePriceAt(i, 'min', e)} showCalculator={false} />,
            },
            {
              title: 'Giá',
              render: (_, { price }, i) => <InputNumber value={price} onChange={(e) => updatePriceAt(i, 'price', e)} />,
            },
            {
              title: '',
              render: (_, __, index) => <Button type='link' onClick={() => deletePriceAt(index)}>Xoá</Button>,
            },
          ]}
        />
      </Modal>
    </div>
  );
};

const PriceOriginalEdit = ({ value = 0, onChange, onChangeAll, disabled = false }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [price, setPrice] = useState(0);

  useEffect(() => {
    setPrice(value || 0);
  }, [value]);

  const handleOk = () => {
    setIsModalOpen(false);
    onChange(price);
  };

  const btnAllClick = () => {
    setIsModalOpen(false);
    onChangeAll(price);
  };

  return (
    <div>
      <Space direction='vertical'>
        {!disabled && <Button type='text' icon={<EditOutlined />} onClick={() => setIsModalOpen(true)} />}
      </Space>

      <Modal
        title='Sửa giá'
        open={isModalOpen}
        onOk={handleOk}
        onCancel={() => setIsModalOpen(false)}
        footer={
          <Space>
            <Button type='primary' onClick={btnAllClick}>Áp dụng chung</Button>
            <Button type='primary' onClick={handleOk}>Ok</Button>
          </Space>
        }
      >
        <InputNumber value={price} onChange={(e) => setPrice(e)} />
      </Modal>
    </div>
  );
};

const PriceBlockDesktop = ({ viewModel, hasMultipleVariants }) => {
  const {
    productVariants,
    pricingEnabled,
    sourceOptions,
    selectedSourceKey,
    setSelectedSourceKey,
    selectedTable,
    isTableSource,
    isTableManualMode,
    isTableAutoMode,
    onVariantPriceListChange,
    onAllVariantPriceListChange,
    onVariantOriginalPriceChange,
    onAllVariantOriginalPriceChange,
  } = viewModel;

  return (
    <Space direction='vertical' size={16} style={{ width: '100%' }}>
      {pricingEnabled && (
        <Space direction='vertical' size={8} style={{ width: '100%' }}>
          <div style={{ fontWeight: 500 }}>Nguồn giá</div>
          <Select value={selectedSourceKey} options={sourceOptions} onChange={setSelectedSourceKey} style={{ width: 420 }} />
          {isTableSource && <Alert type='info' showIcon message={`Đang chỉnh theo bảng giá: ${selectedTable?.name || ''}`} />}
          {isTableAutoMode && <Alert type='warning' showIcon message='Bảng giá tự động chỉ xem theo rule hiện có, không chỉnh sửa tại đây' />}
        </Space>
      )}

      <Table
        pagination={false}
        dataSource={productVariants}
        rowKey={(row) => row?.id || row?.variant_id || row?.variant_title}
        columns={[
          ...(hasMultipleVariants ? [{
            title: 'Biến thể',
            dataIndex: 'variant_title',
            key: 'name',
          }] : []),
          {
            title: 'Giá bán',
            render: (_, { original_price, id: variantID, variant_id }) => (
              <div>
                {formatCurrency(original_price)}
                <PriceOriginalEdit
                  value={original_price ?? 0}
                  onChange={(e) => onVariantOriginalPriceChange(e, variantID || variant_id)}
                  onChangeAll={onAllVariantOriginalPriceChange}
                  disabled={isTableAutoMode}
                />
              </div>
            ),
          },
          {
            title: 'Giá theo số lượng',
            render: (_, { price_list = [], id: variantID, variant_id, original_price }) => (
              <div>
                {(price_list || []).map((x, i) => (
                  <div key={`${x.min}-${x.price}-${i}`} style={{ display: 'flex' }}>
                    <div style={{ width: 75 }}>Từ {x.min}</div>
                    <div style={{ width: 75 }}><b>{formatCurrency(x.price)}</b></div>
                    <div style={{ marginLeft: 16 }}>Giảm {formatCurrency((x.price - original_price) * -1)}</div>
                  </div>
                ))}

                <PriceEdit
                  value={price_list ?? []}
                  original_price={original_price}
                  onChange={(e) => onVariantPriceListChange(e, variantID || variant_id)}
                  onChangeAll={onAllVariantPriceListChange}
                  disabled={isTableAutoMode}
                />
              </div>
            ),
          },
        ]}
      />
    </Space>
  );
};

export default PriceBlockDesktop;
